// FIP Withdrawal Management
import React, { useEffect, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponent/CustomAlertMessage";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";
import DataTableBase from "../../../customComponent/DataTable";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const FIPWithdrawal = () => {
    const [affiliates, setAffiliates] = useState([]);
    const [allData, setAllData] = useState([]);
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
    const [selectedAffiliate, setSelectedAffiliate] = useState(null);
    const [withdrawalData, setWithdrawalData] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [errorRowIndex, setErrorRowIndex] = useState(null);
    const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
    const [transactionNo, setTransactionNo] = useState('');
    const [showWithdrawalListModal, setShowWithdrawalListModal] = useState(false);
    const [withdrawalList, setWithdrawalList] = useState([]);
    const [selectedUserForList, setSelectedUserForList] = useState(null);

    useEffect(() => {
        fetchAffiliates();
    }, []);

    const fetchAffiliates = async () => {
        LoaderHelper.loaderStatus(true);
        try {
            const result = await AuthService.getaffiliateList();
            if (result.success) {
                setAffiliates(result.data || []);
                setAllData(result.data || []);
            }
        } catch {
            alertErrorMessage("Failed to fetch affiliates");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const openWithdrawalModal = async (user) => {
        setSelectedAffiliate(user);
        LoaderHelper.loaderStatus(true);
        try {
            const result = await AuthService.loadInvestments(user._id, 'FIP');
            if (result.success) {
                const investmentsList = result?.affiliateInvestments || [];
                setInvestments(investmentsList);

                // Initialize withdrawal data for each investment
                const initialWithdrawalData = investmentsList.map(inv => {
                    const maxAmt = typeof inv.amount === 'object' ? parseFloat(inv.amount?.$numberDecimal || 0) : parseFloat(inv.amount || 0);
                    return {
                        id: inv._id,
                        investmentId: inv.investmentId || inv._id,
                        type: 'full', // 'full' or 'partial'
                        amount: maxAmt,
                        maxAmount: maxAmt,
                        enabled: false
                    };
                });
                setWithdrawalData(initialWithdrawalData);
                setTransactionDate(new Date().toISOString().split('T')[0]);
                setTransactionNo('');
                setShowWithdrawalModal(true);
            }
        } catch {
            alertErrorMessage("Failed to fetch investments");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const handleCheckboxChange = (index) => {
        const updated = [...withdrawalData];
        updated[index].enabled = !updated[index].enabled;
        if (!updated[index].enabled) {
            // Reset to defaults when disabled
            updated[index].type = 'full';
            updated[index].amount = updated[index].maxAmount;
        }
        setErrorRowIndex(null);
        setWithdrawalData(updated);
    };

    const handleWithdrawalTypeChange = (index, type) => {
        const updated = [...withdrawalData];
        updated[index].type = type;
        if (type === 'full') {
            updated[index].amount = updated[index].maxAmount;
        } else {
            updated[index].amount = '';
        }
        setErrorRowIndex(null);
        setWithdrawalData(updated);
    };

    const handleWithdrawalAmountChange = (index, value) => {
        const updated = [...withdrawalData];
        const amount = parseFloat(value) || 0;

        if (amount > updated[index].maxAmount) {
            alertErrorMessage(`Amount cannot exceed ${updated[index].maxAmount}`);
            setErrorRowIndex(index);
            return;
        }

        updated[index].amount = amount;
        setErrorRowIndex(null);
        setWithdrawalData(updated);
    };

    const getTotalWithdrawal = () => {
        return withdrawalData
            .filter(item => item.enabled)
            .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
            .toFixed(2);
    };

    const openWithdrawalListModal = async (user) => {
        setSelectedUserForList(user);
        LoaderHelper.loaderStatus(true);
        try {
            const result = await AuthService.getFIPWithdrawalList(user._id);
            if (result.success) {
                setWithdrawalList(result.data || []);
                setShowWithdrawalListModal(true);
            } else {
                alertErrorMessage(result.message || "Failed to fetch withdrawal list");
            }
        } catch {
            alertErrorMessage("Failed to fetch withdrawal list");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const handleWithdrawalSubmit = async () => {
        setErrorRowIndex(null);
        const enabledWithdrawals = withdrawalData.filter(w => w.enabled);
        console.log("🚀 ~ enabledWithdrawals:", enabledWithdrawals);

        if (enabledWithdrawals.length === 0) {
            alertErrorMessage("Please select at least one investment for withdrawal");
            return;
        }

        // Validate transaction details
        if (!transactionDate) {
            alertErrorMessage("Please enter transaction date");
            return;
        }

        if (!transactionNo || transactionNo.trim() === '') {
            alertErrorMessage("Please enter transaction number");
            return;
        }

        // Validate each enabled withdrawal
        for (let i = 0; i < withdrawalData.length; i++) {
            const w = withdrawalData[i];
            if (!w.enabled) continue;

            const amount = parseFloat(w.amount);

            if (!w.amount || w.amount === '' || isNaN(amount)) {
                setErrorRowIndex(i);
                alertErrorMessage(`Investment ${w.investmentId}: Please enter a valid withdrawal amount`);
                return;
            }

            if (amount <= 0) {
                setErrorRowIndex(i);
                alertErrorMessage(`Investment ${w.investmentId}: Withdrawal amount must be greater than 0`);
                return;
            }

            if (amount > w.maxAmount) {
                setErrorRowIndex(i);
                alertErrorMessage(`Investment ${w.investmentId}: Withdrawal amount cannot exceed ${w.maxAmount}`);
                return;
            }
        }

        LoaderHelper.loaderStatus(true);
        try {
            // Process withdrawal - replace with actual API call
            const payload = {
                userId: selectedAffiliate._id,
                transactionDate,
                transactionNo,
                withdrawals: enabledWithdrawals
            };
            console.log("Submitting withdrawal payload:", payload);
            const result = await AuthService.fipWithdrawal(payload);
            if(result.success){
                alertSuccessMessage("Withdrawal processed successfully");
                setShowWithdrawalModal(false);
                fetchAffiliates();
            }
            if (!result.success) {
                throw new Error("Withdrawal failed");
            }
        } catch {
            alertErrorMessage("Failed to process withdrawal");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const columns = [
        { name: 'Sr No.', selector: (row, idx) => idx + 1, width: '80px' },
        { name: 'Name', selector: row => `${row.firstName} ${row.lastName}` },
        { name: 'Email', selector: row => row.emailId, width: '200px' },
        { name: 'Total Investment', selector: row => parseFloat(row.total_investment || 0).toFixed(2), width: '140px' },
        { name: 'Self ROI Returned', selector: row => parseFloat(row.total_self_roi_returned || 0).toFixed(2), width: '160px' },
        { name: 'Total ROI to Uplines', selector: row => parseFloat(row.total_upline_roi_returned || 0).toFixed(2), width: '180px' },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-warning" onClick={() => openWithdrawalModal(row)}>
                        Withdrawal
                    </button>
                    <button className="btn btn-sm btn-info" onClick={() => openWithdrawalListModal(row)}>
                        Withdrawal List
                    </button>
                </div>
            )
        }
    ];

    const downloadAffiliateList = (affiliateList, format = 'excel') => {
        if (!affiliateList?.length) {
            alertErrorMessage("No data to export");
            return;
        }

        const finalRows = [];

        affiliateList.forEach(user => {
            const baseRow = {
                'Name': `${user.firstName} ${user.lastName}`,
                'Email': user.emailId,
                'Total Investment': parseFloat(user.total_investment || 0).toFixed(2),
                'Self ROI': parseFloat(user.total_self_roi_returned || 0).toFixed(2),
                'Upline ROI': parseFloat(user.total_upline_roi_returned || 0).toFixed(2)
            };
            finalRows.push(baseRow);
        });

        if (format === 'excel') {
            const ws = XLSX.utils.json_to_sheet(finalRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "FIP Withdrawals");
            XLSX.writeFile(wb, "FIP_Withdrawal_List.xlsx");
        }

        if (format === 'pdf') {
            const doc = new jsPDF();
            doc.text("FIP Withdrawal List", 14, 15);
            doc.autoTable({
                head: [['Name', 'Email', 'Total Investment', 'Self ROI', 'Upline ROI']],
                body: finalRows.map(row => [
                    row['Name'],
                    row['Email'],
                    row['Total Investment'],
                    row['Self ROI'],
                    row['Upline ROI']
                ]),
                startY: 25
            });
            doc.save("FIP_Withdrawal_List.pdf");
        }
    };

    const searchAffiliates = (e) => {
        const term = e.target.value.toLowerCase();
        const keysToSearch = ["emailId", "firstName", "lastName"];
        const filtered = allData.filter(obj =>
            keysToSearch.some(key =>
                obj[key]?.toString().toLowerCase().includes(term)
            )
        );
        setAffiliates(filtered);
    };

    return (
        <div id="layoutSidenav_content">
            <div className="container-xl px-4 mt-4">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5>FIP Withdrawal Management</h5>
                        <div className="col-5">
                            <input
                                style={{ border: "1px solid #ccc" }}
                                className="form-control form-control-solid"
                                type="text"
                                placeholder="Search here..."
                                onChange={searchAffiliates}
                            />
                        </div>
                        <div>
                            <button className="btn btn-success btn-sm mx-2" onClick={() => downloadAffiliateList(affiliates, 'excel')}>
                                Export Excel
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => downloadAffiliateList(affiliates, 'pdf')}>
                                Export PDF
                            </button>
                        </div>
                    </div>

                    <div className="card-body">
                        <DataTableBase columns={columns} data={affiliates} pagination={true} />
                    </div>
                </div>

                {/* Withdrawal Modal */}
                {showWithdrawalModal && selectedAffiliate && (
                    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-xl">
                            <div className="modal-content p-3">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        Process Withdrawal for {selectedAffiliate.firstName} {selectedAffiliate.lastName}
                                    </h5>
                                    <button className="btn-close" onClick={() => setShowWithdrawalModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Select</th>
                                                    <th>Investment ID</th>
                                                    <th>Plan</th>
                                                    <th>Investment Amount</th>
                                                    <th>Currency</th>
                                                    <th>Date</th>
                                                    <th>Withdrawal Type</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {investments.map((inv, idx) => {
                                                    const amount = typeof inv.amount === 'object'
                                                        ? parseFloat(inv.amount?.$numberDecimal || 0)
                                                        : parseFloat(inv.amount || 0);

                                                    return (
                                                        <tr key={idx} className={errorRowIndex === idx ? 'table-danger' : ''}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={withdrawalData[idx]?.enabled || false}
                                                                    onChange={() => handleCheckboxChange(idx)}
                                                                />
                                                            </td>
                                                            <td>{inv.investmentId || inv._id}</td>
                                                            <td>{inv.planDetails?.type || 'N/A'}</td>
                                                            <td>{amount.toFixed(2)}</td>
                                                            <td>{inv.currency}</td>
                                                            <td>{moment(inv.investmentDate).format('DD/MM/YYYY')}</td>
                                                            <td>
                                                                <div>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name={`withdrawal-${idx}`}
                                                                            id={`full-${idx}`}
                                                                            checked={withdrawalData[idx]?.type === 'full'}
                                                                            disabled={!withdrawalData[idx]?.enabled}
                                                                            onChange={() => handleWithdrawalTypeChange(idx, 'full')}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`full-${idx}`}>
                                                                            Full Amount
                                                                        </label>
                                                                    </div>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name={`withdrawal-${idx}`}
                                                                            id={`partial-${idx}`}
                                                                            checked={withdrawalData[idx]?.type === 'partial'}
                                                                            disabled={!withdrawalData[idx]?.enabled}
                                                                            onChange={() => handleWithdrawalTypeChange(idx, 'partial')}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`partial-${idx}`}>
                                                                            Partial Amount
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    value={withdrawalData[idx]?.amount === '' ? '' : withdrawalData[idx]?.amount}
                                                                    readOnly={withdrawalData[idx]?.type === 'full'}
                                                                    disabled={!withdrawalData[idx]?.enabled}
                                                                    onChange={(e) => handleWithdrawalAmountChange(idx, e.target.value)}
                                                                    onWheel={(e) => e.target.blur()}
                                                                    max={amount}
                                                                    placeholder={withdrawalData[idx]?.type === 'partial' ? 'Enter amount' : ''}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                <tr className="table-active ">
                                                    <td className="text-end align-middle"><strong>Date:</strong></td>
                                                    <td>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={transactionDate}
                                                            onChange={(e) => setTransactionDate(e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="text-end align-middle"><strong>Transaction No.:</strong></td>
                                                    <td colSpan={2}>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter transaction number"
                                                            value={transactionNo}
                                                            onChange={(e) => setTransactionNo(e.target.value)}
                                                        />
                                                    </td>
                                                    <td colSpan={2} className="text-end align-middle"><strong>Total Withdrawal Amount:</strong></td>
                                                    <td className="align-middle"><strong>{getTotalWithdrawal()}</strong></td>
                                                </tr>

                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => setShowWithdrawalModal(false)}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-primary" onClick={handleWithdrawalSubmit}>
                                        Process Withdrawal
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Withdrawal List Modal */}
            {showWithdrawalListModal && selectedUserForList && (
                <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content p-3">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Withdrawal History for {selectedUserForList.firstName} {selectedUserForList.lastName}
                                </h5>
                                <button className="btn-close" onClick={() => setShowWithdrawalListModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>Sr No.</th>
                                                <th>Transaction No.</th>
                                                <th>Transaction Date</th>
                                                <th>Investment ID</th>
                                                <th>Withdrawal Type</th>
                                                <th>Investment Amount</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {withdrawalList.length > 0 ? (
                                                withdrawalList.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{item.transaction_number || 'N/A'}</td>
                                                        <td>{item.transaction_date ? moment(item.transaction_date).format('DD MMM YYYY') : 'N/A'}</td>
                                                        <td>{item?.investmentDetails?.investmentId || 'N/A'}</td>
                                                        <td>{item.withdrawal_type || 'N/A'}</td>
                                                        <td>{typeof item?.investment_amount === 'object' ? parseFloat(item?.investment_amount?.$numberDecimal || 0).toFixed(2) : parseFloat(item?.investment_amount || 0).toFixed(2)}</td>
                                                        <td>{typeof item.withdrawal_amount === 'object' ? parseFloat(item.withdrawal_amount?.$numberDecimal || 0).toFixed(2) : parseFloat(item.withdrawal_amount || 0).toFixed(2)}</td>
                                                        <td>
                                                            <span className={`badge bg-${item.status === 'completed' ? 'success' : item.status === 'pending' ? 'warning' : 'danger'}`}>
                                                                {item.status || 'N/A'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center text-muted">
                                                        No withdrawal history found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowWithdrawalListModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}        </div>
    );
};

export default FIPWithdrawal;